import { useClassificacao } from "./hooks/useClassificacao";
import {
  FiAlertTriangle,
  FiChevronLeft,
  FiChevronRight,
  FiEdit3,
  FiImage,
  FiSearch,
  FiTrash2,
} from "react-icons/fi";
import { ExerciseImage } from "../../components/exercises/ExerciseImage";
import ExercicieModal from "../../components/exercises/ExercicieModal";
import Modal from "../../tailadmin/components/ui/Modal";
import Skeleton from "../../components/ui/Skeleton";

export default function ClassificacaoPage() {
  const {
    exercises,
    loading,
    error,
    search,
    page,
    pageSize,
    hasNext,
    totalCount,
    editingExercise,
    deletingExercise,
    deleteLoading,
    setPage,
    setEditingExercise,
    handleSearchChange,
    closeEditModal,
    handleEditSuccess,
    requestDelete,
    cancelDelete,
    confirmDelete,
  } = useClassificacao();

  const hasSearch = search.trim().length > 0;

  return (
    <div className="mx-auto max-w-7xl px-3 sm:px-4 py-4 sm:py-6 md:px-6 2xl:px-10">
      <div className="mb-4 sm:mb-6">
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-red-600 via-rose-600 to-purple-700 p-4 shadow-lg sm:p-6">
          <div className="absolute inset-0 bg-grid-pattern opacity-10"></div>
          <div className="absolute -right-10 -top-10 h-40 w-40 rounded-full bg-white/10 blur-3xl"></div>
          <div className="absolute -bottom-10 -left-10 h-40 w-40 rounded-full bg-white/10 blur-3xl"></div>

          <div className="relative flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-start gap-3 sm:gap-4">
              <div className="rounded-xl bg-white/20 p-2.5 text-white backdrop-blur-sm sm:p-3">
                <FiAlertTriangle size={24} />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white sm:text-3xl">
                  Exercícios rejeitados
                </h2>
                <p className="mt-1 max-w-2xl text-xs text-rose-100 sm:text-sm">
                  Revise apenas exercícios classificados negativamente, corrija
                  informações e exclua itens que não devem permanecer no catálogo.
                </p>
              </div>
            </div>

            <div className="rounded-xl bg-white/15 px-4 py-3 text-white backdrop-blur-sm">
              <div className="text-2xl font-bold leading-none">{totalCount}</div>
              <div className="mt-1 text-xs font-medium text-rose-100">
                rejeitados encontrados
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-stroke bg-white p-4 shadow-sm dark:border-strokedark dark:bg-boxdark sm:p-5">
        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="relative w-full sm:max-w-md">
            <FiSearch
              className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"
              size={18}
            />
            <input
              type="text"
              value={search}
              onChange={(event) => handleSearchChange(event.target.value)}
              placeholder="Buscar por nome, grupo muscular ou equipamento"
              className="w-full rounded-xl border-2 border-stroke bg-white py-3 pl-11 pr-4 text-sm font-medium text-gray-900 outline-none transition-all placeholder:text-gray-400 focus:border-purple-500 focus:ring-4 focus:ring-purple-500/10 dark:border-strokedark dark:bg-boxdark dark:text-white"
            />
          </div>

          <div className="text-sm text-gray-500 dark:text-gray-400">
            Página {page} · até {pageSize} por página
          </div>
        </div>

        {error && (
          <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700 dark:border-red-900/60 dark:bg-red-950/30 dark:text-red-300">
            {error}
          </div>
        )}

        {loading ? (
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
            {Array.from({ length: 6 }).map((_, index) => (
              <div
                key={index}
                className="rounded-2xl border border-stroke bg-gray-50 p-3 dark:border-strokedark dark:bg-boxdark-2"
              >
                <Skeleton className="mb-3 h-44 w-full rounded-xl" />
                <Skeleton className="mb-2 h-5 w-2/3" />
                <Skeleton className="h-4 w-1/2" />
              </div>
            ))}
          </div>
        ) : exercises.length === 0 ? (
          <div className="flex min-h-[320px] flex-col items-center justify-center rounded-2xl border-2 border-dashed border-stroke bg-gray-50 px-6 py-10 text-center dark:border-strokedark dark:bg-boxdark-2">
            <div className="mb-4 rounded-2xl bg-white p-4 text-gray-400 shadow-sm dark:bg-boxdark">
              <FiAlertTriangle size={32} />
            </div>
            <h3 className="text-lg font-bold text-gray-900 dark:text-white">
              {hasSearch
                ? "Nenhum rejeitado encontrado para essa busca"
                : "Nenhum exercício rejeitado"}
            </h3>
            <p className="mt-2 max-w-md text-sm text-gray-500 dark:text-gray-400">
              {hasSearch
                ? "Tente buscar por outro nome, grupo muscular ou equipamento."
                : "Quando um exercício for classificado negativamente, ele aparecerá aqui para revisão."}
            </p>
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
            {exercises.map((exercise) => (
              <article
                key={exercise.id}
                className="group overflow-hidden rounded-2xl border border-stroke bg-white shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-lg dark:border-strokedark dark:bg-boxdark-2"
              >
                <div className="relative h-48 bg-gray-100 dark:bg-gray-800">
                  {exercise.image || exercise.image_url ? (
                    <ExerciseImage
                      exercise={exercise}
                      width={640}
                      height={360}
                      alt={exercise.name}
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center text-gray-300">
                      <FiImage size={40} />
                    </div>
                  )}
                  <span className="absolute left-3 top-3 rounded-full bg-red-600 px-3 py-1 text-xs font-bold uppercase tracking-wide text-white shadow">
                    Rejeitado
                  </span>
                </div>

                <div className="space-y-4 p-4">
                  <div>
                    <h3 className="line-clamp-2 text-base font-bold text-gray-900 dark:text-white">
                      {exercise.name}
                    </h3>
                    {exercise.description && (
                      <p className="mt-1 line-clamp-2 text-sm text-gray-500 dark:text-gray-400">
                        {exercise.description}
                      </p>
                    )}
                  </div>

                  <div className="flex flex-wrap gap-2">
                    {(exercise.muscle_groups ?? exercise.area_muscular ?? [])
                      .slice(0, 3)
                      .map((group) => (
                        <span
                          key={group}
                          className="rounded-full bg-purple-50 px-2.5 py-1 text-xs font-semibold text-purple-700 dark:bg-purple-900/30 dark:text-purple-300"
                        >
                          {group}
                        </span>
                      ))}
                    {(exercise.equipment ?? []).slice(0, 2).map((item) => (
                      <span
                        key={item}
                        className="rounded-full bg-gray-100 px-2.5 py-1 text-xs font-semibold text-gray-700 dark:bg-gray-800 dark:text-gray-300"
                      >
                        {item}
                      </span>
                    ))}
                  </div>

                  <div className="flex gap-2 pt-1">
                    <button
                      type="button"
                      onClick={() => setEditingExercise(exercise)}
                      className="inline-flex flex-1 items-center justify-center gap-2 rounded-xl bg-purple-600 px-4 py-2.5 text-sm font-semibold text-white transition-all hover:bg-purple-700 active:scale-95"
                    >
                      <FiEdit3 size={16} />
                      Editar
                    </button>
                    <button
                      type="button"
                      onClick={() => requestDelete(exercise)}
                      className="inline-flex flex-1 items-center justify-center gap-2 rounded-xl border-2 border-red-200 px-4 py-2.5 text-sm font-semibold text-red-600 transition-all hover:bg-red-50 active:scale-95 dark:border-red-900/60 dark:text-red-300 dark:hover:bg-red-950/30"
                    >
                      <FiTrash2 size={16} />
                      Excluir
                    </button>
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}

        {(page > 1 || hasNext) && (
          <div className="mt-5 flex items-center justify-between border-t border-stroke pt-4 dark:border-strokedark">
            <button
              type="button"
              onClick={() => setPage(page > 1 ? page - 1 : 1)}
              disabled={page === 1 || loading}
              className="inline-flex items-center gap-2 rounded-xl border-2 border-stroke px-4 py-2.5 text-sm font-semibold text-gray-700 transition-all hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-strokedark dark:text-white dark:hover:bg-gray-800"
            >
              <FiChevronLeft size={16} />
              Anterior
            </button>

            <span className="text-sm font-semibold text-gray-600 dark:text-gray-300">
              Página {page}
            </span>

            <button
              type="button"
              onClick={() => setPage(page + 1)}
              disabled={!hasNext || loading}
              className="inline-flex items-center gap-2 rounded-xl border-2 border-stroke px-4 py-2.5 text-sm font-semibold text-gray-700 transition-all hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-strokedark dark:text-white dark:hover:bg-gray-800"
            >
              Próximo
              <FiChevronRight size={16} />
            </button>
          </div>
        )}
      </div>

      <ExercicieModal
        isOpen={editingExercise !== null}
        onBack={closeEditModal}
        data={editingExercise}
        onSuccess={handleEditSuccess}
        allowCatalogEdit
        approveOnSave
      />

      <Modal
        isOpen={deletingExercise !== null}
        onClose={cancelDelete}
        title="Excluir exercício"
      >
        <div className="space-y-5 px-6 pb-6 pt-4 sm:px-8 sm:pb-8">
          <div className="rounded-2xl border border-red-200 bg-red-50 p-4 text-red-800 dark:border-red-900/60 dark:bg-red-950/30 dark:text-red-200">
            <div className="flex items-start gap-3">
              <FiAlertTriangle className="mt-0.5 flex-shrink-0" size={20} />
              <p className="text-sm font-medium">
                Esta ação exclui o exercício definitivamente do catálogo. Não
                será possível desfazer pela interface.
              </p>
            </div>
          </div>

          <p className="text-sm text-gray-600 dark:text-gray-300">
            Confirma a exclusão de{" "}
            <strong className="font-bold text-gray-900 dark:text-white">
              {deletingExercise?.name}
            </strong>
            ?
          </p>

          <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={cancelDelete}
              disabled={deleteLoading}
              className="rounded-xl border-2 border-stroke px-5 py-3 text-sm font-semibold text-gray-700 transition-all hover:bg-gray-50 disabled:opacity-60 dark:border-strokedark dark:text-white dark:hover:bg-gray-800"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={confirmDelete}
              disabled={deleteLoading}
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-red-600 px-5 py-3 text-sm font-semibold text-white transition-all hover:bg-red-700 disabled:opacity-60"
            >
              {deleteLoading ? "Excluindo..." : "Excluir definitivamente"}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}